import { useEffect, useRef, useState } from "react";
import { isIOS } from "@/lib/device";

/**
 * Recover only the visible scene's video. A paused or buffering video is not
 * evidence of an autoplay restriction: show the gesture prompt only when a
 * play attempt on a ready foreground video actually rejects with NotAllowedError.
 */
export function useAutoplayUnlock({
  enabled = true,
  sniffMs = 1400,
  foregroundVideoSelector = '[data-testid="scene-media-video"]',
} = {}) {
  const [needsGesture, setNeedsGesture] = useState(false);
  const [reason, setReason] = useState(null);
  const interactedRef = useRef(false);
  const mountedRef = useRef(false);
  const currentRef = useRef({ enabled, foregroundVideoSelector });
  currentRef.current = { enabled, foregroundVideoSelector };

  // Remember real interactions across scrub/panel pauses. Tracking a gesture
  // while covered must not start any of the deliberately paused media.
  useEffect(() => {
    mountedRef.current = true;
    const rememberGesture = () => { interactedRef.current = true; };
    const events = ["touchstart", "pointerdown", "mousedown", "keydown"];
    events.forEach(event => document.addEventListener(event, rememberGesture, true));
    return () => {
      mountedRef.current = false;
      events.forEach(event => document.removeEventListener(event, rememberGesture, true));
    };
  }, []);

  useEffect(() => {
    setNeedsGesture(false);
    setReason(null);
    if (!enabled) return undefined;

    let disposed = false;
    let sniffElapsed = false;
    let attemptId = 0;
    let recoveryTimer = null;
    let retryTimer = null;
    const foreground = () => document.querySelector(foregroundVideoSelector);
    const stillOwnsPlayback = video => mountedRef.current && currentRef.current.enabled
      && video === document.querySelector(currentRef.current.foregroundVideoSelector);
    const clearPrompt = () => { setNeedsGesture(false); setReason(null); };

    const tryPlay = (video, origin, retryOnce = false) => {
      if (disposed || !video || !stillOwnsPlayback(video)) return;
      if (!video.paused) { clearPrompt(); return; }
      const requestId = ++attemptId;
      const onSuccess = () => {
        // An old effect can finish after a cancelled scrub has resumed this
        // SAME element. Pause only if it is currently covered or no longer
        // foreground, never merely because the old effect was cleaned up.
        if (!stillOwnsPlayback(video)) {
          try { video.pause(); } catch (_) { /* ignore */ }
          return;
        }
        if (!disposed && requestId === attemptId) clearPrompt();
      };
      const onFailure = error => {
        if (disposed || requestId !== attemptId || !stillOwnsPlayback(video) || !video.paused) return;
        if (error?.name === "NotAllowedError" && video.readyState >= 2) {
          // A previous gesture does not guarantee every later video will be
          // permitted. Keep a fresh, confirmed policy rejection actionable.
          const env = isIOS() ? "ios" : ("ontouchstart" in window ? "touch" : "desktop");
          setReason(`${env}:${origin}:autoplay_denied readyState=${video.readyState} networkState=${video.networkState}`);
          setNeedsGesture(true);
        } else if (retryOnce && error?.name !== "NotAllowedError") {
          // Resize can interrupt an otherwise valid play with AbortError.
          // Retry once after settling, without displaying a permission prompt.
          if (retryTimer) clearTimeout(retryTimer);
          retryTimer = setTimeout(() => tryPlay(video, "rotation-retry"), 700);
        }
      };
      try {
        const result = video.play?.();
        if (result?.then) result.then(onSuccess, onFailure);
        else if (!video.paused) onSuccess();
      } catch (error) { onFailure(error); }
    };

    const onPlaying = event => {
      if (disposed || event.target !== foreground()) return;
      attemptId += 1;
      clearPrompt();
    };
    const unlock = () => {
      if (disposed) return;
      interactedRef.current = true;
      clearPrompt();
      tryPlay(foreground(), "gesture");
    };
    const onCanPlay = event => {
      if (event.target !== foreground() || (!sniffElapsed && !interactedRef.current)) return;
      tryPlay(event.target, "canplay");
    };
    const gestureEvents = ["touchstart", "pointerdown", "mousedown", "keydown"];
    gestureEvents.forEach(event => document.addEventListener(event, unlock, true));
    document.addEventListener("playing", onPlaying, true);
    document.addEventListener("canplay", onCanPlay, true);

    const sniffTimer = setTimeout(() => {
      sniffElapsed = true;
      const video = foreground();
      // The next canplay event will check a slow-loading video. Neither its
      // paused state nor an interrupted play promise constitutes a denial.
      if (video?.readyState >= 2) tryPlay(video, "check");
    }, sniffMs);

    const onMaybeRotated = () => {
      if (recoveryTimer) clearTimeout(recoveryTimer);
      recoveryTimer = setTimeout(() => tryPlay(foreground(), "rotation", true), 350);
    };
    window.addEventListener("orientationchange", onMaybeRotated, true);
    window.addEventListener("resize", onMaybeRotated, true);
    document.addEventListener("visibilitychange", onMaybeRotated, true);

    return () => {
      disposed = true;
      clearTimeout(sniffTimer);
      if (recoveryTimer) clearTimeout(recoveryTimer);
      if (retryTimer) clearTimeout(retryTimer);
      window.removeEventListener("orientationchange", onMaybeRotated, true);
      window.removeEventListener("resize", onMaybeRotated, true);
      document.removeEventListener("visibilitychange", onMaybeRotated, true);
      gestureEvents.forEach(event => document.removeEventListener(event, unlock, true));
      document.removeEventListener("playing", onPlaying, true);
      document.removeEventListener("canplay", onCanPlay, true);
    };
  }, [enabled, sniffMs, foregroundVideoSelector]);

  return {
    needsGesture: enabled && needsGesture,
    dismiss: () => { setNeedsGesture(false); setReason(null); },
    reason: enabled ? reason : null,
  };
}
